/**
 * EventDateSelect Component
 * Lets the user pick one specific event (year, then exact date) at an
 * already-chosen hill — used by query types that need a single event
 * rather than a hill's whole history (e.g. "Where in the Bell").
 */

import { useState } from 'react';

function EventDateSelect({ hill, selectedDate, onSelect, disabled }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleYearSubmit = async (e) => {
    e.preventDefault();
    const y = parseInt(year, 10);
    const currentYear = new Date().getFullYear();

    if (!year || isNaN(y) || y < 1900 || y > currentYear) {
      setError(`Please enter a valid year between 1900 and ${currentYear}`);
      setEvents(null);
      return;
    }

    setError('');
    setLoading(true);
    setEvents(null);

    try {
      const response = await fetch(
        `/api/event-dates?year=${y}&hill=${encodeURIComponent(hill)}`,
        { headers: { 'X-API-Key': import.meta.env.VITE_API_KEY || '' } }
      );
      if (!response.ok) throw new Error('Failed to fetch events for that year');
      const data = await response.json();
      setEvents(data);
      if (data.length === 0) setError(`No events found at ${hill} in ${y}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="year-select">
      <h2>Select Event</h2>
      <p className="step-description">Which year was the {hill} event?</p>

      <form onSubmit={handleYearSubmit} className="year-input-form">
        <input
          type="number"
          min="1900"
          max={new Date().getFullYear()}
          value={year}
          onChange={(e) => { setYear(e.target.value); setError(''); }}
          placeholder="e.g. 2026"
          className="year-input"
          disabled={disabled || loading}
        />
        <button type="submit" className="year-submit-button" disabled={disabled || loading || !year}>
          Find Events
        </button>
      </form>

      {error && <p className="year-error">{error}</p>}

      {events && events.length > 0 && (
        <div className="query-type-options">
          {events.map(ev => (
            <button
              key={ev.date}
              className={`query-type-button ${selectedDate === ev.date ? 'selected' : ''}`}
              onClick={() => onSelect(ev.date, ev.event_name)}
              disabled={disabled}
            >
              <h3>{ev.event_name || hill}</h3>
              <p>{new Date(ev.date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default EventDateSelect;
